// FIRST, so Sentry is listening before any other import can throw.
import "@/lib/sentry-init"

import "@hotwired/turbo-rails"
import "@tailwindplus/elements"
import { preventTurboFouce } from "@awesome.me/webawesome"
import { Application } from "@hotwired/stimulus"
import { Current } from "current.js"
import { registerControllers } from "stimulus-vite-helpers"
// Media Chrome audio player components
// Note: mux-audio is a web component that integrates with media-chrome
import "media-chrome"
import "@mux/mux-audio"
import "../../javascript/blurhash"
// Lexxy rich text editor for ActionText
import "@37signals/lexxy"

// Registers the Lucide icon library for Web Awesome's `<wa-icon>`.
import "@/lib/webawesome"
import {
  identifyUser,
  initializePostHog,
  resetUser,
  trackPageView,
} from "@/lib/posthog"
import { setSentryUserFromDocument } from "@/lib/sentry"

preventTurboFouce()

// Initialize Stimulus
const application = Application.start()

// Auto-register all Stimulus controllers using stimulus-vite-helpers
const controllers = import.meta.glob("../controllers/**/*_controller.ts", {
  eager: true,
})
registerControllers(application, controllers)

// Stimulus 3.2's async Application.start() can miss connecting deeply nested
// controllers if an observer error occurs during the initial DOM scan.
// Re-touching data-controller attributes after the first frame ensures the
// MutationObserver picks up any elements that were skipped.
requestAnimationFrame(() => {
  document.querySelectorAll("[data-controller]").forEach((el) => {
    el.setAttribute("data-controller", el.getAttribute("data-controller")!)
  })
})

// Initialize PostHog on first page load and track page views
// turbo:load fires on both initial page load and subsequent Turbo navigations
// Note: initializePostHog() has its own guard to prevent duplicate initialization
document.addEventListener("turbo:load", () => {
  // Turbo merges the new page's head, so the identity meta tags are current
  // here — this is what picks up a sign-in or sign-out.
  setSentryUserFromDocument()

  initializePostHog()

  const user = (Current as Record<string, Record<string, string>>).user ?? {}
  if (user.id) {
    identifyUser(user.id)
  } else {
    // Signing out lands on a signed-out layout, which emits no identity tags.
    // Without this the previous user's identity would keep following the
    // browser, attributing later anonymous activity to them.
    resetUser()
  }

  const pathname = window.location.pathname
  trackPageView(pathname, {
    $pathname: pathname,
    $search: window.location.search,
    $hash: window.location.hash,
  })
})

// A framed Home tab switch never fires `turbo:load`, so it would go
// uncounted (#1913). `window.location` still holds the previous tab here.
document.addEventListener("tab-strip:selected", (event) => {
  const { url } = (event as CustomEvent<{ key: string; url?: string }>).detail
  if (!url) return

  const { pathname, search, hash, href } = new URL(url, window.location.origin)
  trackPageView(pathname, {
    $current_url: href,
    $pathname: pathname,
    $search: search,
    $hash: hash,
  })
})
